import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '@infra/i18n';
import {
  FIRST_PAGE,
  UI_TABLE_PAGE_SIZE_OPTIONS,
  brandColors,
  brandRgba,
  brandShadowRgba,
} from '@shared/constants';
import { PageSizeSelect } from './page-size-select';

const GRADIENT = `linear-gradient(135deg,${brandColors.brandPrimary} 0%,${brandColors.brandPrimaryAlt} 100%)`;
const NAV_SHADOW = `0 2px 8px ${brandShadowRgba('textPrimary', 0.1)}, 0 1px 2px ${brandShadowRgba('textPrimary', 0.06)}, inset 0 1px 0 ${brandShadowRgba('surfaceCard', 0.6)}`;
const GLASS_BG = brandRgba('surfaceCard', 0.85);
const WINDOW_RADIUS = 1;
const COLLAPSE_ABOVE = 7;

const navBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 12px',
  fontSize: 12.5,
  fontWeight: 700,
  borderRadius: 9,
  background: GLASS_BG,
  border: `1px solid ${brandColors.borderStrong}`,
  color: brandColors.textPrimary,
  boxShadow: NAV_SHADOW,
  backdropFilter: 'blur(10px) saturate(160%)',
  transition: 'color .2s ease, border-color .2s ease, background .2s ease, box-shadow .2s ease',
};

const navHover = (disabled: boolean) =>
  disabled
    ? {}
    : {
        onMouseEnter: (e: MouseEvent<HTMLButtonElement>) => {
          const el = e.currentTarget;
          el.style.borderColor = brandColors.brandPrimary;
          el.style.color = brandColors.brandPrimary;
          el.style.background = brandColors.surfaceCard;
          el.style.boxShadow = `0 6px 16px ${brandShadowRgba('brandPrimary', 0.16)}, inset 0 1px 0 ${brandShadowRgba('surfaceCard', 0.6)}`;
        },
        onMouseLeave: (e: MouseEvent<HTMLButtonElement>) => {
          const el = e.currentTarget;
          el.style.borderColor = brandColors.borderStrong;
          el.style.color = brandColors.textPrimary;
          el.style.background = GLASS_BG;
          el.style.boxShadow = NAV_SHADOW;
        },
      };

/** First page, last page and a window around the current one; `null` marks a gap. */
function pageItems(page: number, totalPages: number): (number | null)[] {
  const items: (number | null)[] = [];
  for (let n = FIRST_PAGE; n <= totalPages; n++) {
    const outside =
      totalPages > COLLAPSE_ABOVE &&
      n !== FIRST_PAGE &&
      n !== totalPages &&
      Math.abs(n - page) > WINDOW_RADIUS;
    if (!outside) items.push(n);
    else if (Math.abs(n - page) === WINDOW_RADIUS + 1) items.push(null);
  }
  return items;
}

interface TablePagerProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Left text, e.g. "Showing 1-10 of 42". */
  summary: ReactNode;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  /** Locks the controls while a page is loading. */
  disabled?: boolean;
  prevLabel?: string;
  nextLabel?: string;
  style?: CSSProperties;
}

/** Table footer: range summary, rows-per-page select and numbered page buttons. */
export function TablePager({
  page,
  totalPages,
  onPageChange,
  summary,
  pageSize,
  onPageSizeChange,
  disabled = false,
  prevLabel,
  nextLabel,
  style,
}: TablePagerProps) {
  const { t } = useTranslation();
  const atStart = page === FIRST_PAGE || disabled;
  const atEnd = page === totalPages || disabled;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        flexWrap: 'wrap',
        paddingTop: 16,
        marginTop: 4,
        borderTop: `1px solid ${brandColors.border}`,
        ...style,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: brandColors.textTertiary }}>
          {summary}
        </span>
        <PageSizeSelect
          value={pageSize}
          options={UI_TABLE_PAGE_SIZE_OPTIONS}
          onChange={onPageSizeChange}
          disabled={disabled}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          disabled={atStart}
          onClick={() => onPageChange(Math.max(FIRST_PAGE, page - 1))}
          style={{
            ...navBtn,
            opacity: atStart ? 0.45 : 1,
            cursor: atStart ? 'default' : 'pointer',
          }}
          {...navHover(atStart)}
        >
          <ChevronLeft size={14} /> {prevLabel ?? t('common:buttons.prev')}
        </button>
        {pageItems(page, totalPages).map((n, index) =>
          n === null ? (
            <span
              key={`gap-${index}`}
              style={{ fontSize: 12.5, color: brandColors.textTertiary, padding: '0 2px' }}
            >
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              disabled={disabled}
              onClick={() => onPageChange(n)}
              style={{
                minWidth: 32,
                height: 32,
                borderRadius: 9,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.45 : 1,
                background: n === page ? GRADIENT : 'transparent',
                color: n === page ? brandColors.onDark : brandColors.textSecondary,
                border: n === page ? 'none' : `1px solid ${brandColors.border}`,
              }}
            >
              {n}
            </button>
          )
        )}
        <button
          type="button"
          disabled={atEnd}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          style={{
            ...navBtn,
            opacity: atEnd ? 0.45 : 1,
            cursor: atEnd ? 'default' : 'pointer',
          }}
          {...navHover(atEnd)}
        >
          {nextLabel ?? t('common:buttons.next')} <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
